import classNames from 'classnames';
import BaseLoader from 'components/atoms/base-loader';
import BaseTabs from 'components/atoms/base-tabs';
import BaseButton from 'components/atoms/baseButton';
import React, { useEffect, useState } from 'react';
import CollapseAccordion from 'components/atoms/collapseAccordion';
import ComparerButton from 'components/atoms/icons/comparerButton';
import { createPortal } from 'react-dom';
import { ASSIGN_TOOL_ID } from 'infrastructure/consts';
import { useSetRunbookViewed } from 'infrastructure/hooks/clinic';

import ExpandButton from './components/expand-button';
import { Module, useRunbook } from './hooks/use-run-book';
import RunBooksContent from './components/runbooks-content';
import s from './styles.module.scss';
import RunBookCompareContent from './components/runbook-compare-content';
import Trigger from './components/trigger';

interface IRunBookProps {
  clinicGuid: string;
  patientGuid: string;
}

export const RunBook: React.FC<IRunBookProps> = ({
  clinicGuid,
  patientGuid,
}) => {
  const {
    currentModule,
    expanded,
    isDataEmpty,
    loading,
    formattedUpdatedAt,
    tabs,
    scrollableElementRef,
    instructionData,
    activityLogData,
    handleTabClick,
    setExpanded,
    history,
    hasUpdate,
  } = useRunbook({
    clinicGuid,
    patientGuid,
  });
  const { setRunbookViewed } = useSetRunbookViewed();

  const [runBookOpened, setRunBookOpened] = useState(false);
  const [comparerOpened, setComparerOpened] = useState(false);
  const [hasAssignToolNearby, setHasAssignToolNearby] = useState(false);

  const toggleOpen = () => {
    setRunBookOpened(!runBookOpened);
  };

  const toggleOpenComparer = () => {
    setComparerOpened(!comparerOpened);
  };
  useEffect(() => {
    if (runBookOpened && hasUpdate) {
      setRunbookViewed(clinicGuid);
    }
  }, [runBookOpened, hasUpdate]);

  const ROOT_ELEMENT = document.getElementById('root');
  useEffect(() => {
    const hasAssignTool = !!ROOT_ELEMENT?.querySelector(`#${ASSIGN_TOOL_ID}`);
    setHasAssignToolNearby(hasAssignTool);
  }, []);

  return (
    <>
      {comparerOpened && (
        <RunBookCompareContent
          comparerOpened
          setComparerOpened={setComparerOpened}
          currentData={instructionData}
          history={history}
        />
      )}
      {createPortal(
        <>
          <Trigger
            toggleOpen={toggleOpen}
            hasUpdate={hasUpdate}
            setExpanded={setExpanded}
            isStandalone
            hasAssignToolNearby={hasAssignToolNearby}
          />
          <div
            data-cy="admin-run-book-wrapper"
            id="admin-patient-run-book"
            className={classNames(s['admin-run-book-wrapper'], {
              [s.expanded]: expanded,
              [s.opened]: runBookOpened,
            })}
          >
            <div>
              <div className={s['run-book-wrapper']}>
                <Trigger
                  toggleOpen={toggleOpen}
                  hasUpdate={hasUpdate}
                  setExpanded={setExpanded}
                />

                <div className={s['run-book-body']}>
                  <div className={s['body-header']}>
                    <BaseTabs
                      tabs={tabs}
                      activeKey={currentModule}
                      onChange={(tab) => handleTabClick(tab.key)}
                      size="small"
                      type="rounded"
                    />
                    {currentModule === Module.INSTRUCTION && hasUpdate ? (
                      <BaseButton
                        type="primary"
                        label="Updated"
                        rounded
                        className={s['updated-button']}
                      />
                    ) : (
                      <BaseButton
                        type="secondary"
                        className={s['comparer-button']}
                        onClick={toggleOpenComparer}
                        icon={<ComparerButton />}
                      />
                    )}
                  </div>
                  {loading ? (
                    <BaseLoader loading className={s.loader} />
                  ) : (
                    <div
                      className={s['body-content']}
                      ref={scrollableElementRef}
                    >
                      {currentModule === Module.INSTRUCTION ? (
                        <RunBooksContent
                          isDataEmpty={isDataEmpty}
                          data={instructionData}
                          formattedUpdatedAt={formattedUpdatedAt}
                        />
                      ) : (
                        <CollapseAccordion items={activityLogData} />
                      )}
                    </div>
                  )}
                  <div className={classNames(s['body-actions'])}>
                    <ExpandButton
                      dependencies={[
                        instructionData,
                        activityLogData,
                        currentModule,
                      ]}
                      scrollableElementRef={scrollableElementRef}
                      expanded={expanded}
                      setExpanded={setExpanded}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>,
        ROOT_ELEMENT!,
      )}
    </>
  );
};
